import * as React from 'react';

import * as digitalData from '@ao-internal/digital-data';

import { ChatMessageGroup } from '../components/MessageGroup';
import { ResponseMetaData } from '../components/ResponseMetaData';
import { MatchedResults } from '../components/Results';
import { AddToBasketMessageContent } from '../components/message-types/AddToBasketMessage';
import { ArticleMessageContent } from '../components/message-types/ArticlesMessage';
import { ButtonMessageContent } from '../components/message-types/ButtonMessage';
import { ImageMessageContent } from '../components/message-types/ImageMessage';
import { LinkButtonMessageContent } from '../components/message-types/LinkButtonMessage';
import { PageUpdateMessageContent } from '../components/message-types/PageUpdateMessage';
import { ProductCardMessageContent } from '../components/message-types/ProductCardMessage';
import { RecommendedProductMessageContent } from '../components/message-types/RecommendedProductsMessage';
import { SuggestedPhrasesMessageContent } from '../components/message-types/SuggestedPhrasesMessage';
import { TrackedDeliveryMessageContent } from '../components/message-types/TrackedDeliveryMessage';
import { VideoMessageContent } from '../components/message-types/VideoMessage';
import { ToastNotificationConfig } from '../config/types';
import { isReadOnlyMode } from '../globals';
import {
	getLastPageUpdateFromChatHistory,
	mapToHistoryViewModel,
} from '../helpers/state';
import { getPersistentState, setPersistentState } from '../helpers/storage';
import { Feedback } from '../services/FeedbackService';

export enum SenderTypes {
	Chatbot,
	User,
	System,
	LiveChat,
}

export enum InputTypes {
	Text,
	Interaction,
	Voice,
	Undefined,
}

export enum ChatMessageTypes {
	Simple,
	Button,
	RecommendedProducts,
	LinkButton,
	AddToBasket,
	Tip,
	Image,
	Video,
	SuggestedPhrases,
	TrackedDelivery,
	ProductCard,
	PageUpdate,
	ActivateLiveChat,
	LiveChatStart,
	LiveChatAgentStateChange,
	LiveChatTypingIndicator,
	LiveChatAgentTransferInitiate,
	LiveChatExit,
	Articles,
	LiveChatAutoDisconnectNotice,
	LiveChatAutoDisconnection,
}

export enum ChatMessageStatusTypes {
	Pending,
	Failed,
	Sent,
}

export enum ChatWindowStatusTypes {
	Okay,
	Loading,
	Error,
}

export interface BaseSenderInfo {
	type: SenderTypes;
}

export interface LiveChatSenderInfo extends BaseSenderInfo {
	participantId: string;
}

type ChatMessageContentTypes =
	| SimpleMessageContentType
	| ButtonMessageContent[]
	| RecommendedProductMessageContent[]
	| LinkButtonMessageContent
	| AddToBasketMessageContent
	| TipMessageContentType
	| ImageMessageContent
	| VideoMessageContent
	| SuggestedPhrasesMessageContent[]
	| TrackedDeliveryMessageContent
	| ProductCardMessageContent
	| PageUpdateMessageContent
	| ActivateLiveChatContentType
	| LiveChatStartMessageContentType
	| LiveChatAgentStateChangeMessageContentType
	| LiveChatTypingIndicatorMessageContentType
	| LiveChatAgent
	| ArticleMessageContent[];

export interface BaseChatMessage {
	id: string;
	status?: ChatMessageStatusTypes;
	type: ChatMessageTypes;
	senderInfo?: null | LiveChatSenderInfo;
	content?: ChatMessageContentTypes;
	inputType?: InputTypes;
	requestFeedback?: boolean;
	feedback?: Feedback;
}

export type SimpleMessageContentType = string;
type TipMessageContentType = string;

export interface SimpleChatMessage extends BaseChatMessage {
	type: ChatMessageTypes.Simple;
	content: SimpleMessageContentType;
}

export interface ButtonChatMessage extends BaseChatMessage {
	type: ChatMessageTypes.Button;
	content: ButtonMessageContent[];
}

export interface RecommendedProductsChatMessage extends BaseChatMessage {
	type: ChatMessageTypes.RecommendedProducts;
	content: RecommendedProductMessageContent[];
}

export interface LinkButtonChatMessage extends BaseChatMessage {
	type: ChatMessageTypes.LinkButton;
	content: LinkButtonMessageContent;
}

export interface AddToBasketChatMessage extends BaseChatMessage {
	type: ChatMessageTypes.AddToBasket;
	content: AddToBasketMessageContent;
}

export interface TipChatMessage extends BaseChatMessage {
	type: ChatMessageTypes.Tip;
	content: TipMessageContentType;
}

export interface ImageChatMessage extends BaseChatMessage {
	type: ChatMessageTypes.Image;
	content: ImageMessageContent;
}

export interface VideoChatMessage extends BaseChatMessage {
	type: ChatMessageTypes.Video;
	content: VideoMessageContent;
}

export interface SuggestedPhrasesChatMessage extends BaseChatMessage {
	type: ChatMessageTypes.SuggestedPhrases;
	content: SuggestedPhrasesMessageContent[];
}

export interface TrackedDeliveryChatMessage extends BaseChatMessage {
	type: ChatMessageTypes.TrackedDelivery;
	content: TrackedDeliveryMessageContent;
}

export interface ProductCardChatMessage extends BaseChatMessage {
	type: ChatMessageTypes.ProductCard;
	content: ProductCardMessageContent;
}

export interface PageUpdateChatMessage extends BaseChatMessage {
	type: ChatMessageTypes.PageUpdate;
	content: PageUpdateMessageContent;
}

export interface ActivateLiveChatContentType {
	sessionId: string;
	email: string;
	name: string;
	question: string;
	targetWorkgroup: string;
	validationStatus: string;
	chatUId: string;
	chatInstanceId: string;
}

export interface ActivateLiveChatChatMessage extends BaseChatMessage {
	type: ChatMessageTypes.ActivateLiveChat;
	content: ActivateLiveChatContentType;
}

export interface LiveChatStartMessageContentType {
	chatId: string;
	participantId: string;
}

export interface LiveChatStartChatMessage extends BaseChatMessage {
	type: ChatMessageTypes.LiveChatStart;
	content: LiveChatStartMessageContentType;
}

export interface LiveChatAgent {
	name: string;
	initials: string;
	participantId?: string;
	isConnected: boolean;
}

export interface LiveChatAgentStateChangeMessageContentType
	extends LiveChatAgent {
	state: string;
}

export interface LiveChatAgentStateChangeChatMessage extends BaseChatMessage {
	type: ChatMessageTypes.LiveChatAgentStateChange;
	content: LiveChatAgentStateChangeMessageContentType;
}

export interface LiveChatTypingIndicatorMessageContentType {
	isTyping: boolean;
}

export interface LiveChatTypingIndicatorChatMessage extends BaseChatMessage {
	type: ChatMessageTypes.LiveChatTypingIndicator;
	content: LiveChatTypingIndicatorMessageContentType;
}

export interface LiveChatAgentTransferInitiateChatMessage
	extends BaseChatMessage {
	type: ChatMessageTypes.LiveChatAgentTransferInitiate;
	content: LiveChatAgent;
}

export interface LiveChatExitChatMessage extends BaseChatMessage {
	type: ChatMessageTypes.LiveChatExit;
}

export interface ArticlesChatMessage extends BaseChatMessage {
	type: ChatMessageTypes.Articles;
	content: ArticleMessageContent[];
}

export interface LiveChatAutoDisconnectNoticeChatMessage
	extends BaseChatMessage {
	type: ChatMessageTypes.LiveChatAutoDisconnectNotice;
}

export interface LiveChatAutoDisconnectionChatMessage extends BaseChatMessage {
	type: ChatMessageTypes.LiveChatAutoDisconnection;
}

export type ChatMessage =
	| SimpleChatMessage
	| ButtonChatMessage
	| RecommendedProductsChatMessage
	| LinkButtonChatMessage
	| AddToBasketChatMessage
	| TipChatMessage
	| ImageChatMessage
	| VideoChatMessage
	| SuggestedPhrasesChatMessage
	| TrackedDeliveryChatMessage
	| ProductCardChatMessage
	| PageUpdateChatMessage
	| ActivateLiveChatChatMessage
	| LiveChatStartChatMessage
	| LiveChatAgentStateChangeChatMessage
	| LiveChatTypingIndicatorChatMessage
	| LiveChatAgentTransferInitiateChatMessage
	| LiveChatExitChatMessage
	| ArticlesChatMessage
	| LiveChatAutoDisconnectNoticeChatMessage
	| LiveChatAutoDisconnectionChatMessage;

export type ChatHistoryType = ChatMessageGroup[];

export enum ChatMode {
	ChatBot,
	LiveChat,
}

export enum LiveChatStatus {
	Connecting,
	Connected,
	Transferring,
	Disconnected,
	ConnectionError,
}

type ToastNotificationLogEntry = {
	[key: string]: {
		openedCount?: number;
		lastOpenedTimestamp?: number;
		dismissedTimestamp?: number;
	};
};

export interface State {
	sessionId?: string;
	chatHistory: ChatHistoryType;
	results?: MatchedResults | null | undefined;
	responseMetaData?: ResponseMetaData;
	userInputValue: string;
	showTypingIndicator: boolean;
	sessionInitialised: boolean;
	chatInitialised: boolean;
	chatWindowOpen: boolean;
	chatWindowStatus: ChatWindowStatusTypes;
	lastChatbotMessageType?: ChatMessageTypes;
	breakpoint?: string;
	liveChatActivationData?: ActivateLiveChatContentType;
	chatMode: ChatMode;
	liveChatStatus?: LiveChatStatus;
	liveChatAgent?: LiveChatAgent;
	responsesToProcess: ChatMessage[];
	isTabVisible: boolean;
	isActiveTab: boolean;
	liveChatExitedBy?: SenderTypes;
	liveChatAutoDisconnectNoticeReceivedTimestamp?: number;
	liveChatAutoDisconnectNoticeDismissedTimestamp?: number;
	showLiveChatAutoDisconnectNotice?: boolean;
	lastPageUpdate?: PageUpdateMessageContent;
	lastInteractionTimestamp?: number;
	toastNotification?: ToastNotificationConfig | false;
	toastNotificationDismissed?: boolean;
	toastNotificationsLog?: ToastNotificationLogEntry;
}

export const initialState: State = {
	chatHistory: [],
	userInputValue: '',
	showTypingIndicator: false,
	sessionInitialised: false,
	chatInitialised: false,
	chatWindowOpen: isReadOnlyMode ? true : false,
	chatWindowStatus: ChatWindowStatusTypes.Okay,
	chatMode: ChatMode.ChatBot,
	responsesToProcess: [],
	isTabVisible: true,
	isActiveTab: true,
	showLiveChatAutoDisconnectNotice: false,
};

export type InitialStateOverride = Partial<State>;

interface StoreProviderProps {
	initialStateOverride?: InitialStateOverride;
	children: React.ReactNode;
}

interface AddMessageToChatHistoryActionProps {
	sender: SenderTypes;
	messageData: ChatMessage;
	intent?: string;
}

export enum ActionTypes {
	SET_SESSION_ID,
	SET_RESULTS,
	SET_RESPONSE_META_DATA,
	SET_USER_INPUT_VALUE,
	SET_SHOW_TYPING_INDICATOR,
	SET_SESSION_INITIALISED,
	SET_CHAT_INITIALISED,
	SET_CHAT_WINDOW_OPEN,
	SET_CHAT_WINDOW_STATUS,
	SET_CHAT_HISTORY,
	ADD_MESSAGE_TO_CHAT_HISTORY,
	SET_MESSAGE_STATUS,
	DELETE_USER_MESSAGE,
	SET_LAST_CHATBOT_MESSAGE_TYPE,
	SET_MESSAGE_FEEDBACK,
	SET_BREAKPOINT,
	INITIALISE_CHAT,
	SET_LIVECHAT_ACTIVATION_DATA,
	SET_CHAT_MODE,
	SET_LIVECHAT_STATUS,
	SET_LIVECHAT_AGENT,
	DISCONNECT_LIVECHAT,
	SET_RESPONSES_TO_PROCESS,
	SET_IS_TAB_VISIBLE,
	SET_IS_ACTIVE_TAB,
	SET_LIVECHAT_EXITED_BY,
	SET_LIVECHAT_AUTO_DISCONNECT_NOTICE_RECEIVED_TIMESTAMP,
	SET_LIVECHAT_AUTO_DISCONNECT_NOTICE_DISMISSED_TIMESTAMP,
	SET_LIVECHAT_AUTO_DISCONNECT_NOTICE_IS_VISIBLE,
	SET_SHOULD_ANIMATE_TOGGLE_BUTTON,
	SET_TOAST_OPEN,
	SET_TOAST_CLOSE,
	CREATE_TOAST,
}
export type Action =
	| { type: ActionTypes.SET_SESSION_ID; payload: string }
	| {
			type: ActionTypes.SET_RESULTS;
			payload: MatchedResults | null | undefined;
	  }
	| {
			type: ActionTypes.SET_RESPONSE_META_DATA;
			payload: ResponseMetaData | undefined;
	  }
	| { type: ActionTypes.SET_USER_INPUT_VALUE; payload: string }
	| { type: ActionTypes.SET_SHOW_TYPING_INDICATOR; payload: boolean }
	| { type: ActionTypes.SET_SESSION_INITIALISED; payload: boolean }
	| { type: ActionTypes.SET_CHAT_INITIALISED; payload: boolean }
	| { type: ActionTypes.SET_CHAT_WINDOW_OPEN; payload: boolean }
	| { type: ActionTypes.SET_CHAT_WINDOW_STATUS; payload: ChatWindowStatusTypes }
	| { type: ActionTypes.SET_CHAT_HISTORY; payload: ChatHistoryType }
	| {
			type: ActionTypes.ADD_MESSAGE_TO_CHAT_HISTORY;
			payload: AddMessageToChatHistoryActionProps;
	  }
	| {
			type: ActionTypes.SET_MESSAGE_STATUS;
			payload: { id: string; status: ChatMessageStatusTypes };
	  }
	| { type: ActionTypes.DELETE_USER_MESSAGE; payload: string }
	| {
			type: ActionTypes.SET_LAST_CHATBOT_MESSAGE_TYPE;
			payload: ChatMessageTypes;
	  }
	| {
			type: ActionTypes.SET_MESSAGE_FEEDBACK;
			payload: { id: string; feedback: Feedback };
	  }
	| { type: ActionTypes.SET_BREAKPOINT; payload: string }
	| { type: ActionTypes.INITIALISE_CHAT; payload: string }
	| {
			type: ActionTypes.SET_LIVECHAT_ACTIVATION_DATA;
			payload: ActivateLiveChatContentType;
	  }
	| { type: ActionTypes.SET_CHAT_MODE; payload: ChatMode }
	| {
			type: ActionTypes.SET_LIVECHAT_STATUS;
			payload: LiveChatStatus | undefined;
	  }
	| { type: ActionTypes.SET_LIVECHAT_AGENT; payload: undefined | LiveChatAgent }
	| { type: ActionTypes.DISCONNECT_LIVECHAT }
	| { type: ActionTypes.SET_RESPONSES_TO_PROCESS; payload: ChatMessage[] }
	| { type: ActionTypes.SET_IS_TAB_VISIBLE; payload: boolean }
	| { type: ActionTypes.SET_IS_ACTIVE_TAB; payload: boolean }
	| { type: ActionTypes.SET_LIVECHAT_EXITED_BY; payload?: SenderTypes }
	| {
			type: ActionTypes.SET_LIVECHAT_AUTO_DISCONNECT_NOTICE_RECEIVED_TIMESTAMP;
			payload: number | undefined;
	  }
	| {
			type: ActionTypes.SET_LIVECHAT_AUTO_DISCONNECT_NOTICE_DISMISSED_TIMESTAMP;
			payload: number | undefined;
	  }
	| {
			type: ActionTypes.SET_LIVECHAT_AUTO_DISCONNECT_NOTICE_IS_VISIBLE;
			payload: boolean;
	  }
	| {
			type: ActionTypes.SET_TOAST_OPEN;
			payload: string;
	  }
	| {
			type: ActionTypes.SET_TOAST_CLOSE;
			payload: string;
	  }
	| {
			type: ActionTypes.CREATE_TOAST;
			payload: ToastNotificationConfig;
	  };
type Dispatch = (action: Action) => void;

const ChatStateContext = React.createContext<State | undefined>(undefined);
const ChatDispatchContext = React.createContext<Dispatch | undefined>(
	undefined
);

export const SenderTypesClassMap: { [key: number]: string } = {
	[SenderTypes.Chatbot]: 'chatbot',
	[SenderTypes.User]: 'user',
};

export const ChatMessageTypesClassMap: { [key: number]: string } = {
	[ChatMessageTypes.Simple]: 'simple',
	[ChatMessageTypes.Button]: 'button',
	[ChatMessageTypes.RecommendedProducts]: 'recommended-products',
	[ChatMessageTypes.LinkButton]: 'link-button',
	[ChatMessageTypes.AddToBasket]: 'add-to-basket',
	[ChatMessageTypes.Tip]: 'tip',
	[ChatMessageTypes.Image]: 'image',
	[ChatMessageTypes.Video]: 'video',
	[ChatMessageTypes.SuggestedPhrases]: 'suggested-phrases',
	[ChatMessageTypes.TrackedDelivery]: 'tracked-delivery',
	[ChatMessageTypes.ProductCard]: 'product-card',
	[ChatMessageTypes.PageUpdate]: 'page-update',
	[ChatMessageTypes.Articles]: 'articles',
};

// eslint-disable-next-line max-statements
export function chatReducer(state: State, action: Action): State {
	let newState = state;
	let shouldUpdateLastInteractionCookie = false;

	switch (action.type) {
		case ActionTypes.SET_SESSION_ID:
			newState = {
				...state,
				sessionId: action.payload,
			};
			break;

		case ActionTypes.SET_CHAT_HISTORY: {
			const newChatHistory = action.payload;
			let { lastChatbotMessageType } = state;
			shouldUpdateLastInteractionCookie = true;

			if (newChatHistory.length) {
				const lastMessageGroup = newChatHistory[newChatHistory.length - 1];

				if (lastMessageGroup.sender === SenderTypes.Chatbot) {
					const lastMessage =
						lastMessageGroup.messages[lastMessageGroup.messages.length - 1];
					lastChatbotMessageType = lastMessage.type;
				}
			}

			newState = {
				...state,
				chatHistory: mapToHistoryViewModel(newChatHistory),
				lastChatbotMessageType, // TODO: Compute further down outside of switch statement
			};
			break;
		}

		case ActionTypes.ADD_MESSAGE_TO_CHAT_HISTORY: {
			const { sender, messageData, intent } = action.payload;
			const { senderInfo } = messageData;
			const newChatHistory = [...state.chatHistory];
			const lastMessageGroupIndex = newChatHistory.length - 1;
			const lastMessageGroup = newChatHistory[lastMessageGroupIndex];
			let { lastChatbotMessageType } = state;

			let isSameMessageGroup = false;

			shouldUpdateLastInteractionCookie = true;
			isSameMessageGroup =
				lastMessageGroup && lastMessageGroup.sender === sender;

			if (sender === SenderTypes.LiveChat) {
				isSameMessageGroup =
					lastMessageGroup &&
					lastMessageGroup.liveChatAgent &&
					senderInfo &&
					lastMessageGroup &&
					lastMessageGroup.sender === sender &&
					lastMessageGroup.liveChatAgent.participantId ===
						senderInfo.participantId
						? true
						: false;
			}

			if (isSameMessageGroup) {
				// update the timestamp to "now"
				lastMessageGroup.timestamp = Date.now();
				// append the current message on to the messages props
				lastMessageGroup.messages.push(messageData);
				newChatHistory.splice(lastMessageGroupIndex, 1, lastMessageGroup);
			} else {
				newChatHistory.push({
					messages: [messageData],
					sender,
					timestamp: Date.now(),
					liveChatAgent: state.liveChatAgent,
					intent,
				});
			}

			if (sender === SenderTypes.Chatbot) {
				lastChatbotMessageType = messageData.type;
			}

			newState = {
				...state,
				chatHistory: mapToHistoryViewModel(newChatHistory),
				lastChatbotMessageType, // TODO: Compute further down outside of switch statement
			};

			break;
		}

		case ActionTypes.SET_MESSAGE_STATUS: {
			const { id, status } = action.payload;
			const newChatHistory = state.chatHistory.map((messageGroup) => {
				// if (messageGroup.sender === SenderTypes.User) {
				const messages = [...messageGroup.messages].map((message) => {
					if (message.id && message.id === id) {
						message.status = status;
					}

					return message;
				});

				return { ...messageGroup, messages };
				// }
				// return messageGroup;
			});

			newState = {
				...state,
				chatHistory: newChatHistory,
			};
			break;
		}

		case ActionTypes.DELETE_USER_MESSAGE: {
			const idToDelete = action.payload;
			const chatHistory = [...state.chatHistory];
			const newChatHistory = chatHistory.reduce(
				(acc: ChatHistoryType, messageGroup) => {
					if (messageGroup.sender === SenderTypes.User) {
						const messages = messageGroup.messages.filter(
							(message) => message.id !== idToDelete
						);

						if (messages.length) {
							return [
								...acc,
								{
									...messageGroup,
									messages,
								},
							];
						}

						return acc;
					}
					return [...acc, messageGroup];
				},
				[]
			);

			newState = {
				...state,
				chatHistory: newChatHistory,
			};
			break;
		}

		case ActionTypes.SET_RESULTS:
			newState = {
				...state,
				results: action.payload,
			};
			if (!action.payload) {
				delete newState.results;
			}
			break;

		case ActionTypes.SET_RESPONSE_META_DATA:
			newState = {
				...state,
				responseMetaData: action.payload,
			};
			break;

		case ActionTypes.SET_USER_INPUT_VALUE:
			newState = {
				...state,
				userInputValue: action.payload,
			};
			break;

		case ActionTypes.SET_SHOW_TYPING_INDICATOR:
			newState = {
				...state,
				showTypingIndicator: action.payload,
			};
			break;

		case ActionTypes.SET_CHAT_WINDOW_OPEN:
			shouldUpdateLastInteractionCookie = true;
			newState = {
				...state,
				chatWindowOpen: action.payload,
			};
			break;

		case ActionTypes.SET_CHAT_WINDOW_STATUS:
			newState = {
				...state,
				chatWindowStatus: action.payload,
			};
			break;

		case ActionTypes.SET_SESSION_INITIALISED:
			newState = {
				...state,
				sessionInitialised: action.payload,
			};
			break;

		case ActionTypes.SET_CHAT_INITIALISED:
			newState = {
				...state,
				chatInitialised: action.payload,
			};
			break;

		case ActionTypes.SET_LAST_CHATBOT_MESSAGE_TYPE:
			newState = {
				...state,
				lastChatbotMessageType: action.payload,
			};
			break;

		case ActionTypes.SET_MESSAGE_FEEDBACK: {
			const { id, feedback } = action.payload;
			const newChatHistory = state.chatHistory.map((messageGroup) => {
				const messages = [...messageGroup.messages].map((message) => {
					if (message.id && message.id === id) {
						message.feedback = feedback;
					}

					return message;
				});

				return { ...messageGroup, messages };
			});

			newState = {
				...state,
				chatHistory: newChatHistory,
			};
			break;
		}

		case ActionTypes.SET_BREAKPOINT:
			newState = {
				...state,
				breakpoint: action.payload,
			};
			break;

		case ActionTypes.SET_LIVECHAT_ACTIVATION_DATA:
			newState = {
				...state,
				liveChatActivationData: action.payload,
			};
			break;

		case ActionTypes.SET_CHAT_MODE:
			newState = {
				...state,
				chatMode: action.payload,
			};
			break;

		case ActionTypes.SET_LIVECHAT_STATUS:
			newState = {
				...state,
				liveChatStatus: action.payload,
			};
			break;

		case ActionTypes.SET_LIVECHAT_AGENT:
			newState = {
				...state,
				liveChatAgent: action.payload,
			};

			if (action.payload === undefined) {
				delete newState.liveChatAgent;
			}
			break;

		case ActionTypes.DISCONNECT_LIVECHAT:
			newState = {
				...state,
				chatMode: ChatMode.ChatBot,
				liveChatStatus: LiveChatStatus.Disconnected,
			};
			delete newState.liveChatAgent;
			break;

		case ActionTypes.SET_RESPONSES_TO_PROCESS:
			newState = {
				...state,
				responsesToProcess: action.payload,
			};
			break;

		case ActionTypes.SET_IS_TAB_VISIBLE:
			newState = {
				...state,
				isTabVisible: action.payload,
			};
			break;

		case ActionTypes.SET_IS_ACTIVE_TAB:
			newState = {
				...state,
				isActiveTab: action.payload,
			};
			break;

		case ActionTypes.SET_LIVECHAT_EXITED_BY:
			newState = {
				...state,
				liveChatExitedBy: action.payload,
			};
			break;

		case ActionTypes.SET_LIVECHAT_AUTO_DISCONNECT_NOTICE_RECEIVED_TIMESTAMP:
			newState = {
				...state,
				liveChatAutoDisconnectNoticeReceivedTimestamp: action.payload,
			};
			break;

		case ActionTypes.SET_LIVECHAT_AUTO_DISCONNECT_NOTICE_DISMISSED_TIMESTAMP:
			newState = {
				...state,
				liveChatAutoDisconnectNoticeDismissedTimestamp: action.payload,
			};
			break;

		case ActionTypes.SET_LIVECHAT_AUTO_DISCONNECT_NOTICE_IS_VISIBLE:
			newState = {
				...state,
				showLiveChatAutoDisconnectNotice: action.payload,
			};
			break;

		case ActionTypes.SET_TOAST_OPEN: {
			const targetToastLog = state.toastNotificationsLog
				? state.toastNotificationsLog[action.payload]
				: {};

			newState = {
				...state,
				toastNotificationsLog: {
					...state.toastNotificationsLog,
					[action.payload]: {
						...targetToastLog,
						openedCount:
							targetToastLog && targetToastLog.openedCount
								? targetToastLog.openedCount + 1
								: 1,
						lastOpenedTimestamp: Date.now(),

						dismissedTimestamp: undefined,
					},
				},
				toastNotificationDismissed: false,
			};

			break;
		}

		case ActionTypes.SET_TOAST_CLOSE: {
			const targetToastLog = state.toastNotificationsLog
				? state.toastNotificationsLog[action.payload]
				: {};

			newState = {
				...state,
				toastNotificationsLog: {
					...state.toastNotificationsLog,
					[action.payload]: {
						...targetToastLog,
						dismissedTimestamp: Date.now(),
					},
				},
				toastNotificationDismissed: true,
			};

			break;
		}

		case ActionTypes.CREATE_TOAST:
			newState = {
				...state,
				toastNotification: {
					...action.payload,
				},
				toastNotificationDismissed: false,
			};
			break;

		default:
			break;
	}

	const finalState: State = {
		...newState,
		// Find the latest page update info
		lastPageUpdate: getLastPageUpdateFromChatHistory(state.chatHistory),
		lastInteractionTimestamp: shouldUpdateLastInteractionCookie
			? Date.now()
			: state.lastInteractionTimestamp,
	};

	const shouldUpdateSessionCookie =
		[ActionTypes.SET_IS_TAB_VISIBLE, ActionTypes.SET_IS_ACTIVE_TAB].indexOf(
			action.type
		) < 0;

	if (shouldUpdateSessionCookie) {
		// We only want to save the session cookie for certain action updates,
		// otherwise you can never clear it due to browser tab unload/visibility hidden callback
		setPersistentState(finalState);
	}

	return finalState;
}

export function useChatState() {
	const context = React.useContext(ChatStateContext);

	if (!context) {
		throw new Error(`useChatState must be used within a StoreProvider`);
	}

	return context;
}

export function getActions(dispatch: Dispatch) {
	function setSessionId(id: string): void {
		dispatch({ type: ActionTypes.SET_SESSION_ID, payload: id });
	}

	function setShowTypingIndicator(show: boolean): void {
		dispatch({ type: ActionTypes.SET_SHOW_TYPING_INDICATOR, payload: show });
	}

	function setResults(results: MatchedResults | null | undefined): void {
		// TODO: Move to App?
		if (results) {
			digitalData.manipulateDigitalData({
				page: {
					pageInfo: {
						chatbot: {
							resultsTotal: results.total ? results.total : 0,

							resultsLink: results.link ? results.link : undefined,
						},
					},
				},
			});
		} else {
			digitalData.manipulateDigitalData({
				page: {
					pageInfo: {
						chatbot: {
							resultsTotal: 0,

							resultsLink: undefined,
						},
					},
				},
			});
		}

		dispatch({ type: ActionTypes.SET_RESULTS, payload: results });
	}

	function setResponseMetaData(responseMetaData?: ResponseMetaData): void {
		dispatch({
			type: ActionTypes.SET_RESPONSE_META_DATA,
			payload: responseMetaData,
		});
	}

	function setUserInputValue(value: string): void {
		dispatch({ type: ActionTypes.SET_USER_INPUT_VALUE, payload: value });
	}

	function setSessionInitialised(initialised: boolean): void {
		dispatch({
			type: ActionTypes.SET_SESSION_INITIALISED,
			payload: initialised,
		});
	}

	function setChatInitialised(initialised: boolean): void {
		dispatch({ type: ActionTypes.SET_CHAT_INITIALISED, payload: initialised });
	}

	function setChatWindowOpen(open: boolean): void {
		dispatch({ type: ActionTypes.SET_CHAT_WINDOW_OPEN, payload: open });
	}

	function setChatWindowStatus(status: ChatWindowStatusTypes): void {
		dispatch({ type: ActionTypes.SET_CHAT_WINDOW_STATUS, payload: status });
	}

	function setChatHistory(chatHistory: ChatHistoryType): void {
		dispatch({ type: ActionTypes.SET_CHAT_HISTORY, payload: chatHistory });
	}

	function addMessageToChatHistory(
		options: AddMessageToChatHistoryActionProps
	): void {
		dispatch({
			type: ActionTypes.ADD_MESSAGE_TO_CHAT_HISTORY,
			payload: options,
		});
	}

	function setMessageStatus(id: string, status: ChatMessageStatusTypes): void {
		dispatch({
			type: ActionTypes.SET_MESSAGE_STATUS,
			payload: { id, status },
		});
	}

	function deleteUserMessage(id: string) {
		dispatch({
			type: ActionTypes.DELETE_USER_MESSAGE,
			payload: id,
		});
	}

	function setLastChatbotMessageType(messageType: ChatMessageTypes): void {
		dispatch({
			type: ActionTypes.SET_LAST_CHATBOT_MESSAGE_TYPE,
			payload: messageType,
		});
	}

	function setMessageFeedback(id: string, feedback: Feedback): void {
		dispatch({
			type: ActionTypes.SET_MESSAGE_FEEDBACK,
			payload: { id, feedback },
		});
	}

	function setBreakpoint(breakpoint: string): void {
		dispatch({
			type: ActionTypes.SET_BREAKPOINT,
			payload: breakpoint,
		});
	}

	function setLiveChatActivationData(
		activationData: ActivateLiveChatContentType
	): void {
		dispatch({
			type: ActionTypes.SET_LIVECHAT_ACTIVATION_DATA,
			payload: activationData,
		});
	}

	function setChatMode(mode: ChatMode): void {
		dispatch({
			type: ActionTypes.SET_CHAT_MODE,
			payload: mode,
		});
	}

	function setLiveChatStatus(status: LiveChatStatus | undefined): void {
		dispatch({
			type: ActionTypes.SET_LIVECHAT_STATUS,
			payload: status,
		});
	}

	function setLiveChatAgent(agent: undefined | LiveChatAgent): void {
		dispatch({
			type: ActionTypes.SET_LIVECHAT_AGENT,
			payload: agent,
		});
	}

	function disconnectLiveChat(): void {
		dispatch({
			type: ActionTypes.DISCONNECT_LIVECHAT,
		});
	}

	function setResponsesToProcess(responses: ChatMessage[]): void {
		dispatch({
			type: ActionTypes.SET_RESPONSES_TO_PROCESS,
			payload: responses,
		});
	}

	function setIsTabVisible(isVisible: boolean): void {
		dispatch({
			type: ActionTypes.SET_IS_TAB_VISIBLE,
			payload: isVisible,
		});
	}

	function setIsActiveTab(isActive: boolean): void {
		dispatch({
			type: ActionTypes.SET_IS_ACTIVE_TAB,
			payload: isActive,
		});
	}

	function setLiveChatExitedBy(exitedBy?: SenderTypes): void {
		dispatch({
			type: ActionTypes.SET_LIVECHAT_EXITED_BY,
			payload: exitedBy,
		});
	}

	function setLiveChatAutoDisconnectNoticeReceivedTimestamp(
		timestamp: number | undefined
	): void {
		dispatch({
			type: ActionTypes.SET_LIVECHAT_AUTO_DISCONNECT_NOTICE_RECEIVED_TIMESTAMP,
			payload: timestamp,
		});
	}

	function setLiveChatAutoDisconnectNoticeDismissedTimestamp(
		timestamp: number | undefined
	): void {
		dispatch({
			type: ActionTypes.SET_LIVECHAT_AUTO_DISCONNECT_NOTICE_DISMISSED_TIMESTAMP,
			payload: timestamp,
		});
	}

	function setShowLiveChatAutoDisconnectNotice(isVisible: boolean): void {
		dispatch({
			type: ActionTypes.SET_LIVECHAT_AUTO_DISCONNECT_NOTICE_IS_VISIBLE,
			payload: isVisible,
		});
	}

	function setToastOpen(namespace: string): void {
		dispatch({
			type: ActionTypes.SET_TOAST_OPEN,
			payload: namespace,
		});
	}

	function setToastClose(namespace: string): void {
		dispatch({
			type: ActionTypes.SET_TOAST_CLOSE,
			payload: namespace,
		});
	}

	function createToast(config: ToastNotificationConfig): void {
		dispatch({
			type: ActionTypes.CREATE_TOAST,
			payload: config,
		});
	}

	return {
		setSessionId,
		setResults,
		setResponseMetaData,
		setUserInputValue,
		setShowTypingIndicator,
		setSessionInitialised,
		setChatInitialised,
		setChatWindowOpen,
		setChatWindowStatus,
		setChatHistory,
		addMessageToChatHistory,
		setMessageStatus,
		deleteUserMessage,
		setLastChatbotMessageType,
		setMessageFeedback,
		setBreakpoint,
		setLiveChatActivationData,
		setChatMode,
		setLiveChatStatus,
		setLiveChatAgent,
		disconnectLiveChat,
		setResponsesToProcess,
		setIsTabVisible,
		setIsActiveTab,
		setLiveChatExitedBy,
		setLiveChatAutoDisconnectNoticeReceivedTimestamp,
		setLiveChatAutoDisconnectNoticeDismissedTimestamp,
		setShowLiveChatAutoDisconnectNotice,
		setToastOpen,
		setToastClose,
		createToast,
	};
}

export function useChatDispatch() {
	const dispatch = React.useContext(ChatDispatchContext);

	if (!dispatch) {
		throw new Error('useChatDispatch must be used within a StoreProvider');
	}

	const actions = React.useMemo(() => getActions(dispatch), [dispatch]);

	return { dispatch, ...actions };
}

export function StoreProvider({
	initialStateOverride,
	children,
}: StoreProviderProps) {
	const [state, dispatch] = React.useReducer(chatReducer, {
		...initialState,
		...getPersistentState(),
		...initialStateOverride,
	});

	return (
		<ChatStateContext.Provider value={state}>
			<ChatDispatchContext.Provider value={dispatch}>
				{children}
			</ChatDispatchContext.Provider>
		</ChatStateContext.Provider>
	);
}
